import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setErrorMessage } from 'redux/modules/errorMessage';
import usePrevious from 'hooks/usePrevious';
import {
  getApplicationCreditsCurrencyCode,
  getPricingCreativeCheckoutUrls,
  PRICING_CREATIVE_PATH,
} from 'helpers/ApplicationCreditsHelper';
import { trackEvent } from 'helpers/Analytics';

const APPLICATION_CREDITS_FAILURE_MESSAGE =
  'Your application credits payment was cancelled';

const useApplicationCreditsCheckout = ({
  user,
  currency,
  getApplicationCreditsCheckoutToken,
  applicationCreditsCheckoutToken,
  applicationCreditsCheckoutError,
  redirectToStripeCheckout,
  checkoutUrls = getPricingCreativeCheckoutUrls(),
}) => {
  const dispatch = useDispatch();
  const prevApplicationCreditsCheckoutToken = usePrevious(
    applicationCreditsCheckoutToken
  );

  useEffect(() => {
    if (
      applicationCreditsCheckoutToken &&
      applicationCreditsCheckoutToken !== prevApplicationCreditsCheckoutToken
    ) {
      redirectToStripeCheckout(applicationCreditsCheckoutToken);
    }
  }, [
    applicationCreditsCheckoutToken,
    prevApplicationCreditsCheckoutToken,
    redirectToStripeCheckout,
  ]);

  useEffect(() => {
    if (!applicationCreditsCheckoutError) {
      return;
    }

    const message =
      applicationCreditsCheckoutError?.errors?.message?.[0] ||
      applicationCreditsCheckoutError?.message ||
      'Unable to start application credits checkout';

    dispatch(setErrorMessage(message));
  }, [applicationCreditsCheckoutError, dispatch]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (!params.has('payment-credits-failure')) {
      return;
    }

    dispatch(setErrorMessage(APPLICATION_CREDITS_FAILURE_MESSAGE));
    window.history.replaceState(null, null, window.location.pathname);
  }, [dispatch]);

  const redirectToCreditsCheckout = (event) => {
    event?.preventDefault();

    if (!user) {
      window.location.href = `/signin?referer=${PRICING_CREATIVE_PATH}`;
      return;
    }

    trackEvent('payg-purchase-started', {
      currency: getApplicationCreditsCurrencyCode(currency),
    });

    try {
      getApplicationCreditsCheckoutToken({
        user_id: user.id,
        currency: getApplicationCreditsCurrencyCode(currency),
        ...checkoutUrls,
      });
    } catch (err) {
      console.error(err);
    }
  };

  return { redirectToCreditsCheckout };
};

export default useApplicationCreditsCheckout;
